import { AnimatePresence, motion } from "framer-motion";
import { Brain, ChevronDown, ChevronUp } from "lucide-react";
import moment from "moment";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { CustomReasoning } from "../specs/customReasoning";
import { MessagePart } from "../utils/messageHelpers";
// import { ToolTimeline } from "./ToolTimeline";

interface CustomReasoningWidgetProps {
  reasoningSteps: CustomReasoning[];
  toolEvents?: MessagePart[]; // Tool input streaming events
  toolParts?: MessagePart[]; // Tool parts for ToolTimeline
  onApprove?: (approvalId: string) => Promise<void>;
  onDeny?: (approvalId: string) => Promise<void>;
  isStreaming?: boolean;
}

/**
 * Token-based streaming animation hook
 *
 * Animates text in 3-4 character chunks (tokens) for realistic streaming effect.
 * Uses 50ms delay per token for fast, visible animation similar to Claude Code.
 */
function useTypingEffect(
  text: string,
  isStreaming: boolean,
  speed: number = 50
) {
  const [streamedText, setStreamedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  // Chunk text into tokens (3-4 characters each)
  const chunkIntoTokens = useCallback((text: string): string[] => {
    const tokens: string[] = [];
    let i = 0;
    while (i < text.length) {
      const chunkSize = Math.floor(Math.random() * 2) + 3; // 3-4 chars
      tokens.push(text.slice(i, i + chunkSize));
      i += chunkSize;
    }
    return tokens;
  }, []);

  useEffect(() => {
    // If not streaming, show full text immediately
    if (!isStreaming) {
      setStreamedText(text);
      setIsComplete(true);
      return;
    }

    // Check if new text is an extension of current text (new reasoning added)
    // If so, only animate the NEW portion
    const isExtension = text.startsWith(streamedText);

    if (isExtension && streamedText.length > 0) {
      // New text was appended - only animate the new portion
      const newTextPortion = text.slice(streamedText.length);

      if (newTextPortion.length === 0) {
        // No new text, nothing to animate
        return;
      }

      setIsComplete(false);

      const tokens = chunkIntoTokens(newTextPortion);
      let currentIndex = 0;
      let accumulated = streamedText; // Start with existing text

      const timer = setInterval(() => {
        if (currentIndex < tokens.length) {
          accumulated += tokens[currentIndex];
          setStreamedText(accumulated);
          currentIndex++;
        } else {
          setIsComplete(true);
          clearInterval(timer);
        }
      }, speed);

      return () => clearInterval(timer);
    } else {
      // Complete text change - reset and animate from scratch
      setStreamedText("");
      setIsComplete(false);

      const tokens = chunkIntoTokens(text);
      let currentIndex = 0;
      let accumulated = "";

      const timer = setInterval(() => {
        if (currentIndex < tokens.length) {
          accumulated += tokens[currentIndex];
          setStreamedText(accumulated);
          currentIndex++;
        } else {
          setIsComplete(true);
          clearInterval(timer);
        }
      }, speed);

      return () => clearInterval(timer);
    }
  }, [text, isStreaming, speed, chunkIntoTokens]);

  return { streamedText, isComplete };
}

/**
 * Custom Reasoning Widget
 *
 * Displays AI reasoning steps in a collapsible interface with:
 * - Brain icon with pulsing animation during streaming
 * - Token-based streaming animation (3-4 char chunks at 50ms for visible typing effect)
 * - "Thinking..." status while streaming
 * - "Thought for X seconds" duration after completion
 * - Smooth collapse/expand animations with framer-motion
 *
 * Design follows AI SDK Elements patterns with black/gray color scheme.
 */
export const CustomReasoningWidget = memo(function CustomReasoningWidget({
  reasoningSteps,
  toolEvents = [],
  toolParts = [],
  // onApprove,
  // onDeny,
  isStreaming = false,
}: CustomReasoningWidgetProps) {
  // Auto-expand immediately when streaming starts OR when there's content
  const [isExpanded, setIsExpanded] = useState(true);

  // Keep expanded while streaming or when content exists
  useEffect(() => {
    if (
      isStreaming ||
      reasoningSteps.length > 0 ||
      toolEvents.length > 0 ||
      toolParts.length > 0
    ) {
      setIsExpanded(true);
    }
  }, [isStreaming, reasoningSteps.length, toolEvents.length, toolParts.length]);

  // Calculate duration using moment.js
  const duration = useMemo(() => {
    if (reasoningSteps.length === 0) return 0;
    const firstTime = moment(reasoningSteps[0].timestamp);
    const lastTime = moment(
      reasoningSteps[reasoningSteps.length - 1].timestamp
    );
    return Math.round(lastTime.diff(firstTime, "seconds"));
  }, [reasoningSteps]);

  // Combine reasoning steps and tool events into a unified thinking display
  const fullThinkingText = useMemo(() => {
    const sections: string[] = [];

    // Add reasoning steps
    if (reasoningSteps.length > 0) {
      const reasoningText = reasoningSteps
        .map((step) => `💭 ${step.thought}`)
        .join("\n\n");
      sections.push(reasoningText);
    }

    // Add tool events - show all tool-related streaming information
    if (toolEvents.length > 0) {
      const toolTexts: string[] = [];

      // Process tool-call events
      toolEvents
        .filter((event) => event.type === "tool-call")
        .forEach((event) => {
          const toolName = event.toolName || event.tool || "Unknown Tool";
          toolTexts.push(`🔧 Preparing to call: ${toolName}`);
        });

      // Process tool-input-start events
      toolEvents
        .filter((event) => event.type === "tool-input-start")
        .forEach((event) => {
          const toolName = event.toolName || event.tool || "Unknown Tool";
          const inputPreview = event.input
            ? `\n  Input: ${JSON.stringify(event.input, null, 2)}`
            : "";
          toolTexts.push(`🔧 Calling Tool: ${toolName}${inputPreview}`);
        });

      // Note: step-start events removed - they don't provide useful information

      if (toolTexts.length > 0) {
        sections.push(toolTexts.join("\n\n"));
      }
    }

    return sections.join("\n\n---\n\n");
  }, [reasoningSteps, toolEvents]);

  // Token-based streaming animation
  const { streamedText, isComplete } = useTypingEffect(
    fullThinkingText,
    isStreaming,
    15 // 15ms per token for very fast streaming animation
  );

  // Early return check AFTER all hooks
  // Show widget if: (1) has content OR (2) actively streaming (to show "Thinking..." immediately)
  if (
    reasoningSteps.length === 0 &&
    toolEvents.length === 0 &&
    toolParts.length === 0 &&
    !isStreaming
  )
    return null;

  // Status text based on streaming state
  const statusText = isStreaming
    ? "Thinking..."
    : duration > 0
    ? `Thought for ${duration} ${duration === 1 ? "second" : "seconds"}`
    : "Thought process";

  return (
    <div className="w-full border border-border-primary rounded-lg bg-surface-primary shadow-sm overflow-hidden mt-3 mb-3">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-surface-hover transition-colors"
      >
        <div className="flex items-center gap-2">
          {/* Brain icon with pulsing animation during streaming */}
          <motion.div
            animate={
              isStreaming
                ? {
                    scale: [1, 1.1, 1],
                    opacity: [0.7, 1, 0.7],
                  }
                : {}
            }
            transition={
              isStreaming
                ? {
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }
                : {}
            }
          >
            <Brain className="w-5 h-5 text-content-primary" />
          </motion.div>

          <div className="flex flex-col items-start">
            <span className="text-footnote text-content-tertiary">
              {statusText}
            </span>
          </div>
        </div>

        {/* Expand/collapse icon */}
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-content-tertiary" />
        ) : (
          <ChevronDown className="w-4 h-4 text-content-tertiary" />
        )}
      </button>

      {/* Expandable content with smooth animation */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="p-3 border-t border-border-secondary space-y-3">
              {/* Reasoning text - show content OR loading indicator */}
              {streamedText || fullThinkingText ? (
                <div className="footnote text-content-secondary whitespace-pre-wrap font-mono bg-surface-tertiary p-3 rounded-lg border border-border-primary leading-relaxed">
                  {isStreaming ? (
                    <>
                      {streamedText}
                      {/* Blinking cursor during streaming */}
                      {!isComplete && (
                        <motion.span
                          animate={{ opacity: [1, 0, 1] }}
                          transition={{
                            duration: 0.8,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                          className="inline-block ml-0.5 text-content-primary"
                        >
                          |
                        </motion.span>
                      )}
                    </>
                  ) : (
                    fullThinkingText
                  )}
                </div>
              ) : isStreaming ? (
                // Loading indicator when streaming but no content yet
                <div className="text-callout text-content-secondary font-mono bg-surface-tertiary p-3 rounded-lg border border-border-primary">
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="flex items-center gap-2"
                  >
                    <div className="flex gap-1">
                      <motion.span
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 0,
                        }}
                      >
                        •
                      </motion.span>
                      <motion.span
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 0.2,
                        }}
                      >
                        •
                      </motion.span>
                      <motion.span
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 0.4,
                        }}
                      >
                        •
                      </motion.span>
                    </div>
                    <span className="text-content-tertiary">
                      Loading thoughts...
                    </span>
                  </motion.div>
                </div>
              ) : null}

              {/* Tool Timeline - embedded within reasoning widget */}
              {/* {toolParts.length > 0 && (
                <div className="border-t border-border-secondary pt-3">
                  <div className="text-caption-one font-semibold text-content-secondary mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Tool Execution
                  </div>
                  <ToolTimeline
                    toolParts={toolParts}
                    onApprove={onApprove}
                    onDeny={onDeny}
                  />
                </div>
              )} */}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
